import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NavBarComponent from "../../navBar/NavBarComponent";
import FooterComponent from "../../home/FooterComponent";
import ModernPricingView from "../../pricing/modernPricingView";
import { sendSlackMessageTracked } from "../../../models/notifications";

// ─── Inline SVG icons ────────────────────────────────────────────────────────

function ShieldIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
    );
}

function CreditIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
            <line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
    );
}

function AlertIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
    );
}

function RefreshIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
        </svg>
    );
}

function CheckIcon({ color = "#22c55e" }) {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <polyline points="20 6 9 17 4 12"/>
        </svg>
    );
}

function XIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
    );
}

// ─── Sections ─────────────────────────────────────────────────────────────────

function Hero({ onCTA }) {
    return (
        <section style={{ background: "#050505", padding: "100px 24px 80px", textAlign: "center" }}>
            <div style={{ maxWidth: "720px", margin: "0 auto" }}>
                <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: "18px" }}>
                    Chargeback Prevention
                </p>
                <h1 style={{ fontSize: "clamp(32px, 5vw, 56px)", fontWeight: 800, color: "#fff", lineHeight: 1.1, marginBottom: "22px" }}>
                    Stop chargebacks before they hit your merchant account
                </h1>
                <p style={{ fontSize: "17px", color: "#9ca3af", lineHeight: 1.65, maxWidth: "560px", margin: "0 auto 36px" }}>
                    When a customer opens a dispute, the window to resolve it is short. Pabloo lets you offer store credit the moment a complaint is raised, so the customer withdraws the claim instead of escalating it to their bank.
                </p>
                <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                    <button
                        onClick={onCTA}
                        style={{ padding: "14px 28px", background: "#22c55e", color: "#fff", border: "none", borderRadius: "10px", fontSize: "14px", fontWeight: 700, cursor: "pointer" }}
                    >
                        Start free trial
                    </button>
                    <a
                        href="/pricing"
                        style={{ padding: "14px 28px", background: "transparent", color: "#e5e7eb", border: "1px solid #2a2a2a", borderRadius: "10px", fontSize: "14px", fontWeight: 600, cursor: "pointer", textDecoration: "none" }}
                    >
                        See pricing
                    </a>
                </div>
            </div>
        </section>
    );
}

function StatsStrip() {
    const stats = [
        { value: "$25–$100", label: "average chargeback fee per dispute" },
        { value: "1%", label: "dispute rate triggers payment processor review" },
        { value: "86%", label: "of disputes are resolved before bank escalation with store credit offers" },
        { value: "3x", label: "more likely to keep the customer after a credit resolution" },
    ];
    return (
        <section style={{ background: "#111", borderTop: "1px solid #1a1a1a", borderBottom: "1px solid #1a1a1a", padding: "40px 24px" }}>
            <div style={{ maxWidth: "960px", margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "32px", textAlign: "center" }}>
                {stats.map((s) => (
                    <div key={s.label}>
                        <div style={{ fontSize: "28px", fontWeight: 800, color: "#22c55e", marginBottom: "6px" }}>{s.value}</div>
                        <div style={{ fontSize: "12.5px", color: "#6b7280", lineHeight: 1.5 }}>{s.label}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function ProblemSection() {
    const problems = [
        {
            icon: <AlertIcon />,
            title: "Fees pile up fast",
            body: "Each chargeback costs between $25 and $100 in processing fees alone, before you account for lost revenue and the cost of the product already shipped.",
        },
        {
            icon: <ShieldIcon />,
            title: "Your processor is watching",
            body: "Stripe, PayPal, and Shopify Payments all monitor dispute rates. Exceeding 1% can trigger account reviews, reserve holds, or termination.",
        },
        {
            icon: <CreditIcon />,
            title: "Standard refunds don't help",
            body: "Issuing a cash refund after a dispute is filed doesn't cancel the chargeback. The bank process continues, and you still pay the fee.",
        },
    ];
    return (
        <section style={{ background: "#0a0a0a", padding: "80px 24px" }}>
            <div style={{ maxWidth: "900px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "56px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        The Problem
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Chargebacks are expensive, and they compound
                    </h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px" }}>
                    {problems.map((p) => (
                        <div key={p.title} style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "16px", padding: "28px" }}>
                            <div style={{ marginBottom: "16px" }}>{p.icon}</div>
                            <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#fff", marginBottom: "10px" }}>{p.title}</h3>
                            <p style={{ fontSize: "13.5px", color: "#9ca3af", lineHeight: 1.65 }}>{p.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function HowItWorksSection() {
    const steps = [
        {
            num: "01",
            title: "Customer raises a complaint",
            body: "A customer contacts you about an order issue or opens a dispute in their payment app. This is the critical window before it reaches the bank.",
        },
        {
            num: "02",
            title: "You offer store credit instantly",
            body: "Using Pabloo, you issue store credit in seconds, directly to the customer's account. No manual codes or coupon workarounds.",
        },
        {
            num: "03",
            title: "Customer withdraws the chargeback",
            body: "The customer accepts the store credit and cancels their bank dispute. No chargeback fee, no payment processor flag, no revenue lost.",
        },
        {
            num: "04",
            title: "Customer spends the credit",
            body: "The store credit brings them back. They place another order to use it, turning a near-dispute into a repeat purchase.",
        },
    ];
    return (
        <section style={{ background: "#050505", padding: "80px 24px" }}>
            <div style={{ maxWidth: "860px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "56px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        How It Works
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Resolve disputes in four steps
                    </h2>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {steps.map((step, i) => (
                        <div key={step.num} style={{ display: "flex", gap: "24px", alignItems: "flex-start", background: "#111", border: "1px solid #1e1e1e", borderRadius: "14px", padding: "24px 28px" }}>
                            <div style={{ fontSize: "22px", fontWeight: 800, color: "#22c55e", minWidth: "36px", lineHeight: 1 }}>{step.num}</div>
                            <div>
                                <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#fff", marginBottom: "6px" }}>{step.title}</h3>
                                <p style={{ fontSize: "13.5px", color: "#9ca3af", lineHeight: 1.6, margin: 0 }}>{step.body}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ComparisonSection() {
    const rows = [
        { label: "Prevents chargeback fee", credit: true, refund: false, ignore: false },
        { label: "Keeps the revenue", credit: true, refund: false, ignore: false },
        { label: "Protects your dispute rate", credit: true, refund: false, ignore: false },
        { label: "Retains the customer", credit: true, refund: false, ignore: false },
        { label: "Drives a repeat purchase", credit: true, refund: false, ignore: false },
        { label: "Works before bank escalation", credit: true, refund: false, ignore: false },
    ];
    return (
        <section style={{ background: "#0a0a0a", padding: "80px 24px" }}>
            <div style={{ maxWidth: "820px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "48px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        Why Store Credit
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Store credit outperforms every other response to a dispute
                    </h2>
                </div>
                <div style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "16px", overflow: "hidden" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 140px 140px", borderBottom: "1px solid #1e1e1e", padding: "14px 24px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.1em" }}></div>
                        <div style={{ fontSize: "12px", fontWeight: 700, color: "#22c55e", textAlign: "center" }}>Store credit</div>
                        <div style={{ fontSize: "12px", fontWeight: 700, color: "#9ca3af", textAlign: "center" }}>Cash refund</div>
                        <div style={{ fontSize: "12px", fontWeight: 700, color: "#9ca3af", textAlign: "center" }}>Do nothing</div>
                    </div>
                    {rows.map((row, i) => (
                        <div
                            key={row.label}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 140px 140px 140px",
                                padding: "14px 24px",
                                borderBottom: i < rows.length - 1 ? "1px solid #1a1a1a" : "none",
                                alignItems: "center",
                            }}
                        >
                            <div style={{ fontSize: "13.5px", color: "#d1d5db" }}>{row.label}</div>
                            <div style={{ display: "flex", justifyContent: "center" }}><CheckIcon /></div>
                            <div style={{ display: "flex", justifyContent: "center" }}><XIcon /></div>
                            <div style={{ display: "flex", justifyContent: "center" }}><XIcon /></div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function FeaturesSection() {
    const features = [
        {
            icon: <RefreshIcon />,
            title: "Issue credit in under 10 seconds",
            body: "No need to open a spreadsheet or generate coupon codes. Find the customer in Pabloo and issue credit directly to their account.",
        },
        {
            icon: <ShieldIcon />,
            title: "Works with any Shopify store",
            body: "Pabloo plugs into your existing Shopify setup. No custom dev work, no theme edits required.",
        },
        {
            icon: <CreditIcon />,
            title: "Credit balances customers actually use",
            body: "Store credit issued through Pabloo shows up at checkout automatically. Customers don't need a code or a reminder.",
        },
        {
            icon: <AlertIcon />,
            title: "Full audit trail",
            body: "Every credit event is logged with a timestamp, amount, and reason. You have a clear record if a dispute does escalate.",
        },
    ];
    return (
        <section style={{ background: "#050505", padding: "80px 24px" }}>
            <div style={{ maxWidth: "900px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "56px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        Built For This
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Tools that make dispute resolution fast
                    </h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px" }}>
                    {features.map((f) => (
                        <div key={f.title} style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "16px", padding: "28px" }}>
                            <div style={{ marginBottom: "16px" }}>{f.icon}</div>
                            <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#fff", marginBottom: "8px" }}>{f.title}</h3>
                            <p style={{ fontSize: "13.5px", color: "#9ca3af", lineHeight: 1.65, margin: 0 }}>{f.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function FinalCTA({ onCTA }) {
    return (
        <section style={{ background: "#0a0a0a", borderTop: "1px solid #1a1a1a", padding: "80px 24px", textAlign: "center" }}>
            <div style={{ maxWidth: "560px", margin: "0 auto" }}>
                <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2, marginBottom: "16px" }}>
                    Start preventing chargebacks today
                </h2>
                <p style={{ fontSize: "15px", color: "#9ca3af", lineHeight: 1.6, marginBottom: "32px" }}>
                    14-day free trial. No setup fees. Works with your existing Shopify store in minutes.
                </p>
                <button
                    onClick={onCTA}
                    style={{ padding: "15px 32px", background: "#22c55e", color: "#fff", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}
                >
                    Start free trial
                </button>
            </div>
        </section>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function NoChargebacksComponent() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
        sendSlackMessageTracked("Lead is visiting the No Chargebacks page.", "#website-updates");
    }, []);

    const handleCTA = () => { window.location.href = "https://app.pabloo.com/sign-up"; };

    return (
        <div style={{ background: "#050505" }}>
            <NavBarComponent />
            <Hero onCTA={handleCTA} />
            <StatsStrip />
            <ProblemSection />
            <HowItWorksSection />
            <ComparisonSection />
            <FeaturesSection />
            <ModernPricingView />
            <FinalCTA onCTA={handleCTA} />
            <FooterComponent />
        </div>
    );
}
